import { observer } from "mobx-react-lite"
import { useCallback, useRef, useState } from "react"


import { DropdownFilterInput } from "@/components/DropdownMenu/DropdownFilterInput.tsx"
import { DropdownMenuContent, DropdownMenuSeparator } from "@/components/DropdownMenu/DropdownMenu.tsx"
import { DropdownWithFilterModel } from "@/components/DropdownMenu/DropdownWithFilterModel.ts"
import { DropdownWithFilterStyles } from "@/components/DropdownMenu/DropdownWithFilterStyles.ts"
import { BatchLoader } from "@/components/IntersectionObserver/BatchLoader.tsx"
import { Scrollable } from "@/components/Scrollable/Scrollable.tsx"
import { ScrollableModelImpl } from "@/components/Scrollable/ScrollableModel.ts"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { useModel } from "@/hooks/useModel.ts"
import { ellipsize } from "@/styles/textStyles.ts"

import type { FetchUsingResult } from "@/hooks/useBatchLoader.ts"
import type { ReactNode } from "react"

export const DropdownWithFilter = observer(<T, >(props: {
  items: FetchUsingResult<T>
  inputValue: string
  setInputValue: (value: string) => void
  renderItem: (item: T, index: number) => ReactNode
  noItemsFound?: (value: string) => ReactNode
  beforeInput?: ReactNode
  placeholder?: ReactNode
  header?: ReactNode
  footer?: ReactNode
  maxHeight?: number
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const scrollableModel = useModel(() => new ScrollableModelImpl())
  const model = useModel(() => new DropdownWithFilterModel(scrollableModel), [scrollableModel])

  const handleInputValueChange = (value: string) => {
    props.setInputValue(value)
    model.onInputValueChange(value)
    inputRef.current?.focus()
  }

  const isFirstLoad = model.isFirstLoad.get()
  const allLoaded = props.items.allLoaded

  const [offset, setOffset] = useState<number>(0)

  // Resize the dropdown menu if it doesn't fit the screen
  const onPlaced = useCallback(() => {
    const contentRect = contentRef.current?.getBoundingClientRect()

    if (contentRect && contentRect.top < 0) {
      setOffset(-contentRect.top)
    }
  }, [])

  return (
    <DropdownMenuContent
      ref={contentRef}
      side="top"
      align="start"
      onPlaced={onPlaced}
      avoidCollisions={false}
      onCloseAutoFocus={(e) => e.preventDefault()}
      onEscapeKeyDown={(e) => e.stopPropagation()}
    >
      <DropdownWithFilterStyles.Container offset={offset} maxHeight={props.maxHeight}>
        <DropdownFilterInput
          ref={inputRef}
          setInputValue={handleInputValueChange}
          beforeInput={props.beforeInput}
        />
        <DropdownMenuSeparator />
        <Scrollable
          className={DropdownWithFilterStyles.List}
          childrenContainerClassName={DropdownWithFilterStyles.Scrollable}
          model={scrollableModel}
        >
          {props.header}
          <DropdownWithFilterStyles.Header>
            {allLoaded === null || allLoaded.length === 0
              ? (props.inputValue !== ""
                  ? (isFirstLoad ? <Spinner text={ellipsize("Loading")} /> : props.noItemsFound?.(props.inputValue))
                  : props.placeholder
              )
              : null
            }
          </DropdownWithFilterStyles.Header>
          {allLoaded?.map((file, index) => {
            return props.renderItem(file, index)
          })}
          <BatchLoader
            setBatch={props.items.setBatch}
            batchSize={props.items.batchSize}
            nextOffset={props.items.nextOffset}
            hasMore={props.items.hasMore}
          />
        </Scrollable>
        <DropdownWithFilterStyles.Footer>{props.footer}</DropdownWithFilterStyles.Footer>
      </DropdownWithFilterStyles.Container>
    </DropdownMenuContent>
  )
})
